import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import ConfirmationModal from "../modals/ConfirmationModal";
import "./WhitelabelSubmissionDetail.scss";

interface WhitelabelSubmissionData {
  id: string;
  appName: string;
  status: "Submitted" | "Rejected" | "Approved";
  appNameSDK: string;
  appNameBackend: string;
  endCustomerName: string;
  devTeamSize: string;
  devTeamLocation: string;
  applicationVersion: string;
  accessControlSystem: string;
  hidOrigoApiVersion: string;
  programmingLanguage: string;
  hostingEnvironment: string;
  postValidationDeployment: string;
  plannedProductionLaunchDate: string;
  endUserUseCases: string;
  sequenceFlowDiagram: string | null;
}

interface WhitelabelSubmissionDetailProps {
  isHIDUser: boolean;
  submissionId: string;
  onBackToList: () => void;
}

interface DetailRow {
  label: string;
  value: string | React.ReactNode;
}

const WhitelabelSubmissionDetail: React.FC<WhitelabelSubmissionDetailProps> = ({
  isHIDUser,
  submissionId,
  onBackToList,
}) => {
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  // Static sample data - would be replaced with API data based on submissionId
  const submissionData: WhitelabelSubmissionData = useMemo(() => {
    // Mock data based on submissionId
    const mockData: Record<string, WhitelabelSubmissionData> = {
      "1": {
        id: "1",
        appName: "ABC",
        status: "Submitted",
        appNameSDK: "Lorem ipsum dolo",
        appNameBackend: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam",
        endCustomerName: "Dolor sit amet, consectetur",
        devTeamSize: "3",
        devTeamLocation: "Manchester",
        applicationVersion: "V0.04",
        accessControlSystem: "Ad minim veniam",
        hidOrigoApiVersion: "3",
        programmingLanguage: "C++",
        hostingEnvironment: "Azure",
        postValidationDeployment: "Ad minim veniam",
        plannedProductionLaunchDate: "13/06/2026",
        endUserUseCases: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam",
        sequenceFlowDiagram: "sequence_flow.pdf",
      },
      "2": {
        id: "2",
        appName: "XYZ",
        status: "Rejected",
        appNameSDK: "XYZ SDK App",
        appNameBackend: "XYZ Backend System",
        endCustomerName: "Test Customer",
        devTeamSize: "5",
        devTeamLocation: "London",
        applicationVersion: "V1.0",
        accessControlSystem: "OAuth 2.0",
        hidOrigoApiVersion: "2",
        programmingLanguage: "Java",
        hostingEnvironment: "AWS",
        postValidationDeployment: "Cloud deployment",
        plannedProductionLaunchDate: "15/07/2026",
        endUserUseCases: "Enterprise use case",
        sequenceFlowDiagram: "xyz_flow.pdf",
      },
      "3": {
        id: "3",
        appName: "123",
        status: "Approved",
        appNameSDK: "123 Mobile App",
        appNameBackend: "123 API System",
        endCustomerName: "Enterprise Client",
        devTeamSize: "8",
        devTeamLocation: "New York",
        applicationVersion: "V2.1",
        accessControlSystem: "SAML",
        hidOrigoApiVersion: "3",
        programmingLanguage: "Python",
        hostingEnvironment: "On-Premises",
        postValidationDeployment: "Internal deployment",
        plannedProductionLaunchDate: "01/09/2026",
        endUserUseCases: "Access control for corporate buildings",
        sequenceFlowDiagram: "123_diagram.pdf",
      },
    };
    return mockData[submissionId] || mockData["1"];
  }, [submissionId]);

  const isEditable = isHIDUser && submissionData.status === "Submitted";

  const getStatusChip = (status: WhitelabelSubmissionData["status"]) => {
    let className = "";
    switch (status) {
      case "Submitted":
        className = "status-submitted";
        break;
      case "Rejected":
        className = "status-rejected";
        break;
      case "Approved":
        className = "status-approved";
        break;
    }
    return <Chip label={status} className={`status-chip ${className}`} />;
  };

  const detailRows: DetailRow[] = [
    { label: "Status:", value: getStatusChip(submissionData.status) },
    { label: "App name for SDK:", value: submissionData.appNameSDK },
    { label: "App name for backend System/IAM system/headend system (API):", value: submissionData.appNameBackend },
    { label: "End customer name:", value: submissionData.endCustomerName },
    { label: "Dev team size:", value: submissionData.devTeamSize },
    { label: "Dev team location:", value: submissionData.devTeamLocation },
    { label: "Application version:", value: submissionData.applicationVersion },
    { label: "Access control system:", value: submissionData.accessControlSystem },
    { label: "Version of HID Origo API currently using:", value: submissionData.hidOrigoApiVersion },
    { label: "Programming language used for API integration:", value: submissionData.programmingLanguage },
    { label: "Hosting environment for API integration:", value: submissionData.hostingEnvironment },
    { label: "Application deployment to the users post validation:", value: submissionData.postValidationDeployment },
    { label: "Planned production launch date:", value: submissionData.plannedProductionLaunchDate },
    { label: "End user use case(s) intended for this application:", value: submissionData.endUserUseCases },
    {
      label: "Sequence flow diagram:",
      value: submissionData.sequenceFlowDiagram ? (
        <a href="#" className="view-file-link" onClick={(e) => e.preventDefault()}>
          View file
        </a>
      ) : (
        "Not provided"
      ),
    },
  ];

  const handleCancel = () => {
    onBackToList();
  };

  const handleApprove = () => {
    setIsApproveModalOpen(true);
  };

  const handleReject = () => {
    setIsRejectModalOpen(true);
  };

  const handleConfirmApprove = () => {
    console.log("Approving submission:", submissionId);
    setIsApproveModalOpen(false);
    onBackToList();
    // TODO: API call to approve
  };

  const handleConfirmReject = () => {
    console.log("Rejecting submission:", submissionId);
    setIsRejectModalOpen(false);
    onBackToList();
    // TODO: API call to reject
  };

  return (
    <div className="whitelabel-submission-detail-container">
      <div className="detail-content-wrapper">
        <div className="detail-header">
          <h2>{submissionData.appName}</h2>
        </div>

        <button
          type="button"
          className="back-link"
          onClick={onBackToList}
        >
          <Iconography icon="arrowLeft" size={IconographySizes.Small} />
          <span>BACK TO WHITELABEL SUBMISSIONS</span>
        </button>

        <div className="detail-table-area">
          <TableContainer component={Paper} className="detail-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="overview-column">Overview</TableCell>
                  <TableCell className="details-column">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="overview-column">{row.label}</TableCell>
                    <TableCell className="details-column">{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      <div className="detail-footer">
        <Button
          variant="flat"
          label="Cancel"
          normalCase={true}
          onClick={handleCancel}
        />
        {isEditable && (
          <div className="footer-right-actions">
            <Button
              variant="flat"
              label="Reject"
              normalCase={true}
              onClick={handleReject}
            />
            <Button
              variant="primary"
              label="Approve"
              normalCase={true}
              onClick={handleApprove}
            />
          </div>
        )}
      </div>

      {isApproveModalOpen && (
        <ConfirmationModal
          title="Approve submission"
          message="Are you sure you want to approve this whitelabel submission?"
          confirmLabel="Yes, approve"
          cancelLabel="Cancel"
          onConfirm={handleConfirmApprove}
          onCancel={() => setIsApproveModalOpen(false)}
        />
      )}

      {isRejectModalOpen && (
        <ConfirmationModal
          title="Reject submission"
          message="Are you sure you want to reject this whitelabel submission?"
          confirmLabel="Yes, reject"
          cancelLabel="Cancel"
          onConfirm={handleConfirmReject}
          onCancel={() => setIsRejectModalOpen(false)}
        />
      )}
    </div>
  );
};

export default WhitelabelSubmissionDetail;

